import { useTranslation } from "react-i18next"
import { ConversationFlagResponseDataSchema } from "schema"
import { z } from "zod"
import { ConversationFlagKeyBadge } from "./ConversationFlagKeyBadge"

type ConversationFlag = z.infer<typeof ConversationFlagResponseDataSchema>

type Props = {
  flags: ConversationFlag[]
  scope: "message" | "conversation"
}

function groupFlags(flags: ConversationFlag[]) {
  const groups = new Map<string, { key: string; count: number }>()

  for (const flag of flags) {
    const group = groups.get(flag.key)
    if (group) {
      group.count += 1
    } else {
      groups.set(flag.key, { key: flag.key, count: 1 })
    }
  }

  return [...groups.values()]
}

export const ConversationFlags = ({ flags, scope }: Props) => {
  const { t } = useTranslation()
  const flagsByKind = {
    Issue: groupFlags(flags.filter((flag) => flag.kind === "Issue")),
    Friction: groupFlags(flags.filter((flag) => flag.kind === "Friction")),
  }

  if (flags.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">
        {scope === "message"
          ? t("conversations.chat.flags.noData")
          : t("conversations.chat.flags.noConversationData")}
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {(["Issue", "Friction"] as const).map((kind) => {
        const flagsForKind = flagsByKind[kind]
        if (flagsForKind.length === 0) return null

        return (
          <div key={kind} className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {flagsForKind.map((flagGroup) => (
                <ConversationFlagKeyBadge
                  key={flagGroup.key}
                  flagKey={flagGroup.key}
                  kind={kind}
                  count={flagGroup.count}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
